import { useEffect, useRef, useState } from 'react';
import { loadFaceApi, computeDescriptor } from '@/lib/faceApi';

/**
 * Webcam preview + one-shot face capture. Loads the face engine, streams the
 * camera, and on capture returns a 128-float descriptor to the parent. The image
 * itself never leaves the browser — only the descriptor is passed to onCapture.
 */
export default function FaceScanner({
  onCapture,
  busy = false,
  ctaLabel = 'Capture face',
}: {
  onCapture: (descriptor: number[]) => void | Promise<void>;
  busy?: boolean;
  ctaLabel?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState('Starting camera…');
  const [ready, setReady] = useState(false);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        await loadFaceApi();
        if (cancelled) return;
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 360 } },
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
        setStatus('Position your face in the frame, then capture.');
      } catch (cause) {
        setStatus(cause instanceof Error ? cause.message : 'Camera unavailable — allow camera access and reload.');
      }
    };
    start();
    return () => { cancelled = true; streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, []);

  const capture = async () => {
    if (!videoRef.current || scanning || busy) return;
    setScanning(true);
    setStatus('Scanning…');
    try {
      const descriptor = await computeDescriptor(videoRef.current);
      if (!descriptor) { setStatus('No face detected — move closer and ensure good, even lighting.'); return; }
      setStatus('Face captured.');
      await onCapture(descriptor);
    } catch (cause) {
      setStatus(cause instanceof Error ? cause.message : 'Scan failed — please retry.');
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-full max-w-sm aspect-[4/3] bg-black rounded-xl overflow-hidden">
        <video ref={videoRef} muted playsInline className="w-full h-full object-cover [transform:scaleX(-1)]" />
        <div className="absolute inset-6 border-2 border-white/40 rounded-[40%] pointer-events-none" />
        {!ready && <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">Starting camera…</div>}
      </div>
      <p className="text-xs text-gray-500 mt-3 text-center min-h-[16px]">{status}</p>
      <button
        onClick={capture}
        disabled={!ready || scanning || busy}
        className="mt-3 px-6 py-2.5 bg-[#0B6E77] text-white text-sm font-semibold rounded-lg hover:bg-[#085258] disabled:opacity-50 cursor-pointer whitespace-nowrap"
      >
        <i className="ri-camera-line mr-1.5" />{scanning || busy ? 'Working…' : ctaLabel}
      </button>
    </div>
  );
}
